// src/ApiPanel/pages/ApiPanelTokenManager.jsx
// Token & API Request -> Token.
//
// Rewired to the REAL endpoint. The previous version talked to invented
// /api_panel/tokens routes and modelled a list of IP/token pairs; the actual
// API has no such thing. `api_partner_action` manages exactly one key per
// partner, through three actions:
//
//   GET_API_KEY       -> { api_key, has_api_key, callback_url, key_created_at }
//   GENERATE_API_KEY  -> issues a new key AND revokes the previous one's
//                        session server-side, so the old key dies instantly
//   REVOKE_API_KEY    -> clears the key
//
// Two things about that key worth knowing, because they change how this
// screen behaves:
//
// 1. It is a JWT with a ten-year lifetime, not a short opaque string. It
//    carries the partner's identity and is stored encrypted server-side. A
//    leaked key is a full account credential valid until 2036, which is why
//    this screen makes regeneration prominent and warns before it.
//
// 2. GENERATE returns the key in the response and that is the only clean
//    moment to copy it. GET_API_KEY does return it again, but a screen that
//    re-displays a live credential on every page load is a screen that leaks
//    it over someone's shoulder — so it is masked by default and revealing it
//    is a deliberate click.
import React, { useCallback, useEffect, useState } from "react";
import {
  Card, Notice, ReportPage, prettyDate,
} from "../components/reports/ReportKit";
import { useAction } from "../hooks/useReportQuery";
import { apiPanelFetch } from "../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../constants/apiPanelAuth";
import { useApiPanelSession } from "../context/ApiPanelSessionContext";

const mask = (token) => {
  if (!token) return "";
  const s = String(token);
  if (s.length <= 16) return "•".repeat(s.length);
  return `${s.slice(0, 8)}${"•".repeat(24)}${s.slice(-6)}`;
};

export default function ApiPanelTokenManager() {
  const { user } = useApiPanelSession();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  // Set only by a GENERATE call in this session — the one moment the key is
  // meant to be copied.
  const [freshKey, setFreshKey] = useState("");
  const action = useAction();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiPanelFetch(API_PANEL_ENDPOINTS.API_PARTNER_ACTION, {
        method: "POST",
        form: { action: "GET_API_KEY" },
      });
      setInfo(res?.data || null);
    } catch (err) {
      setLoadError(err.message || "Could not load your API key status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    const first = !info?.has_api_key;
    const warning = first
      ? "Generate an API key for this account?"
      : "Generate a NEW API key?\n\nThe current key stops working immediately — " +
        "any server still using it will start failing straight away. Make sure " +
        "you can deploy the new one before you continue.";
    if (!window.confirm(warning)) return;

    const res = await action.run(
      API_PANEL_ENDPOINTS.API_PARTNER_ACTION,
      { action: "GENERATE_API_KEY" },
      { form: true }
    );
    if (res?.api_key) {
      setFreshKey(res.api_key);
      setRevealed(false);
      load();
    }
  };

  const revoke = async () => {
    if (!window.confirm(
      "Revoke this API key?\n\nEvery integration using it stops working " +
      "immediately, and it cannot be restored — you would have to generate a " +
      "new one and redeploy."
    )) return;
    const res = await action.run(
      API_PANEL_ENDPOINTS.API_PARTNER_ACTION,
      { action: "REVOKE_API_KEY" },
      { form: true }
    );
    if (res) {
      setFreshKey("");
      setRevealed(false);
      load();
    }
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked (insecure context, permissions) — the key is on
         screen and selectable, so this is a convenience, not the only path */
    }
  };

  const hasKey = !!info?.has_api_key;

  return (
    <ReportPage
      title="Token & API Request"
      subtitle="The API key this account authenticates its server-to-server calls with."
    >
      {freshKey && (
        <Card className="apip-freshkey">
          <div className="apip-card-header">
            <span>Your new API key</span>
            <span className="apip-badge apip-badge-warn">Copy it now</span>
          </div>
          <div className="apip-key-body">
            <Notice tone="warn">
              This is the one moment it is on screen for you to copy cleanly.
              Store it in your server&rsquo;s secrets manager or environment
              variables — never in source control, a mobile app bundle, or a
              browser bundle. Anyone holding it can act as this account.
            </Notice>
            <div className="apip-key-value apip-mono">{freshKey}</div>
            <div className="apip-form-actions">
              <button type="button" className="apip-btn apip-btn-primary" onClick={() => copy(freshKey)}>
                <i className={copied ? "iconoir-check" : "iconoir-copy"} />
                {copied ? "Copied" : "Copy key"}
              </button>
              <button type="button" className="apip-btn apip-btn-ghost" onClick={() => setFreshKey("")}>
                I&rsquo;ve stored it — hide
              </button>
            </div>
          </div>
        </Card>
      )}

      {action.message && !freshKey && <Notice tone="ok">{action.message}</Notice>}
      {action.error && <Notice tone="bad">{action.error}</Notice>}
      {loadError && <Notice tone="bad">{loadError}</Notice>}

      <Card title="API key">
        {loading ? (
          <div className="apip-loading">Loading…</div>
        ) : (
          <div className="apip-key-body">
            <dl className="apip-kv apip-kv-2">
              <dt>Account</dt><dd>{user?.name || "—"}</dd>
              <dt>Status</dt>
              <dd>
                {hasKey
                  ? <span className="apip-badge apip-badge-ok">Active</span>
                  : <span className="apip-badge">Not generated</span>}
              </dd>
              <dt>Created</dt><dd>{info?.key_created_at || "—"}</dd>
              <dt>Callback URL</dt>
              <dd className="apip-mono">{info?.callback_url || "not set"}</dd>
            </dl>

            {hasKey && info?.api_key && (
              <>
                <div className="apip-key-value apip-mono">
                  {revealed ? info.api_key : mask(info.api_key)}
                </div>
                <div className="apip-form-actions">
                  <button type="button" className="apip-btn apip-btn-ghost" onClick={() => setRevealed((v) => !v)}>
                    <i className={revealed ? "iconoir-eye-off" : "iconoir-eye"} />
                    {revealed ? "Hide" : "Reveal"}
                  </button>
                  <button type="button" className="apip-btn apip-btn-ghost" onClick={() => copy(info.api_key)}>
                    <i className={copied ? "iconoir-check" : "iconoir-copy"} /> {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </>
            )}

            <div className="apip-form-actions">
              <button type="button" className="apip-btn apip-btn-primary" onClick={generate} disabled={action.busy}>
                <i className="iconoir-key-alt-plus" />
                {action.busy ? "Working…" : hasKey ? "Regenerate key" : "Generate key"}
              </button>
              {hasKey && (
                <button
                  type="button"
                  className="apip-btn apip-btn-ghost apip-btn-danger"
                  onClick={revoke}
                  disabled={action.busy}
                >
                  <i className="iconoir-trash" /> Revoke
                </button>
              )}
              <button type="button" className="apip-btn apip-btn-ghost" onClick={load} disabled={loading}>
                <i className="iconoir-refresh" /> Refresh
              </button>
            </div>
          </div>
        )}
      </Card>

      <Card title="Using the key">
        <div className="apip-key-body">
          <p className="apip-prose">
            Send it as a bearer token on every server-to-server call:
          </p>
          <pre className="apip-code">{`Authorization: Bearer <your API key>`}</pre>
          <Notice tone="warn">
            This key is a ten-year credential, not a short-lived token. Three
            consequences worth planning around: put it in server-side config
            only, never in a mobile app or front-end bundle where it can be
            extracted; regenerate it here if you suspect it has leaked, which
            kills the old one instantly; and if your provider lets you pin the
            calling IP, do that too, so a stolen key is not enough on its own.
          </Notice>
        </div>
      </Card>
    </ReportPage>
  );
}
